import { useCallback, useEffect, useState } from "react";
import { Loader2, Tag, Trash2, Pencil, X, Percent, DollarSign } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { code: "", kind: "percent", value: "", active: true };

export const DiscountsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/discounts");
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error("Enter a code."); return; }
    const value = Number(form.value);
    if (isNaN(value) || value <= 0) { toast.error("Enter a valid amount."); return; }
    if (form.kind === "percent" && value > 100) { toast.error("Percent can't be over 100."); return; }
    setSaving(true);
    try {
      const payload = { code: form.code.trim(), kind: form.kind, value, active: !!form.active };
      if (editingId) {
        await api.put(`/admin/discounts/${editingId}`, payload);
        toast.success("Discount updated");
      } else {
        await api.post("/admin/discounts", payload);
        toast.success("Discount code created");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save the discount.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (d) => {
    try {
      await api.put(`/admin/discounts/${d.id}`, { active: !d.active });
      load();
    } catch {
      toast.error("Couldn't update the code.");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete code ${d.code}?`)) return;
    try {
      await api.delete(`/admin/discounts/${d.id}`);
      toast.success("Discount deleted");
      if (editingId === d.id) { setEditingId(null); setForm(EMPTY); }
      load();
    } catch {
      toast.error("Couldn't delete the code.");
    }
  };

  const edit = (d) => {
    setEditingId(d.id);
    setForm({ code: d.code, kind: d.kind, value: String(d.value), active: !!d.active });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const describe = (d) => (d.kind === "percent" ? `${d.value}% off` : `$${Number(d.value).toFixed(2)} off`);

  return (
    <div data-testid="discounts-tab" className="space-y-8">
      <div>
        <h3 className="font-display text-2xl tracking-[0.04em] text-ink">DISCOUNT CODES</h3>
        <p className="text-navy/60 text-sm mt-1">Create codes customers can enter in their cart at checkout. Discounts apply to the merchandise subtotal (not shipping).</p>
      </div>

      <form onSubmit={submit} data-testid="discount-form" className="bg-white border border-line p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-display text-lg tracking-[0.04em]">{editingId ? "Edit discount code" : "Add a discount code"}</h4>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }} data-testid="discount-cancel-edit" className="text-navy/50 hover:text-navy"><X size={18} /></button>
          )}
        </div>
        <div className="grid sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">CODE</label>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="discount-code-input"
              className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy tracking-[0.08em]" placeholder="SPIRIT10" maxLength={30} />
          </div>
          <div>
            <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">TYPE</label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="discount-kind-select"
              className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy bg-white">
              <option value="percent">Percent off (%)</option>
              <option value="fixed">Dollars off ($)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs tracking-[0.14em] text-navy/60 font-semibold mb-1.5">AMOUNT</label>
            <div className="flex items-center border border-line focus-within:border-navy">
              <span className="pl-4 pr-1 text-navy/50 text-sm">{form.kind === "percent" ? <Percent size={13} /> : <DollarSign size={13} />}</span>
              <input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="discount-value-input" inputMode="decimal"
                className="w-full py-3 pr-4 text-sm outline-none" placeholder={form.kind === "percent" ? "10" : "5.00"} />
            </div>
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="discount-active-toggle" className="w-4 h-4 accent-[#14110f]" />
              Active (customers can use it)
            </label>
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="discount-submit-button"
          className="mt-5 flex items-center gap-2 bg-gold text-ink px-6 py-3 text-xs font-bold tracking-[0.14em] hover:bg-gold-soft transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Tag size={15} />}
          {saving ? "SAVING…" : editingId ? "SAVE CHANGES" : "CREATE CODE"}
        </button>
      </form>

      {loading ? (
        <p className="text-navy/50 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-navy/50 text-sm" data-testid="discounts-empty">No discount codes yet — create one above.</p>
      ) : (
        <div className="space-y-2" data-testid="discounts-list">
          {items.map((d) => (
            <div key={d.id} className="bg-white border border-line px-5 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`discount-row-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="bg-ink text-gold text-xs font-bold tracking-[0.12em] px-3 py-1.5">{d.code}</span>
                <span className="text-sm text-navy/70">{describe(d)}</span>
                <span className={`text-[10px] font-bold tracking-[0.12em] px-2 py-0.5 ${d.active ? "bg-green-100 text-green-800" : "bg-navy/10 text-navy/50"}`} data-testid={`discount-status-${d.id}`}>
                  {d.active ? "ACTIVE" : "OFF"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggle(d)} data-testid={`discount-toggle-${d.id}`}
                  className="text-xs font-bold tracking-[0.1em] px-3 py-2 border border-line text-navy/70 hover:border-navy hover:text-navy transition-colors">
                  {d.active ? "TURN OFF" : "TURN ON"}
                </button>
                <button onClick={() => edit(d)} data-testid={`discount-edit-${d.id}`} className="p-2 text-navy/50 hover:text-navy"><Pencil size={15} /></button>
                <button onClick={() => remove(d)} data-testid={`discount-delete-${d.id}`} className="p-2 text-navy/40 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
